import React from 'react';
import {
  Activity,
  BarChart3,
  Bell,
  MessageSquare,
  ChevronRight,
  Shield,
  Zap,
} from 'lucide-react';
import { useTranslation } from '@/i18n';
import { clsx } from 'clsx';

interface WelcomePageProps {
  onGetStarted: () => void;
}

export const WelcomePage: React.FC<WelcomePageProps> = ({ onGetStarted }) => {
  const { t } = useTranslation();

  const features = [
    {
      icon: BarChart3,
      title: t.welcome.featurePredictiveTitle,
      description: t.welcome.featurePredictiveDesc,
      color: 'text-blue-500 dark:text-blue-400',
      bg: 'bg-blue-50 dark:bg-blue-900/30',
    },
    {
      icon: Activity,
      title: t.welcome.featureMetricsTitle,
      description: t.welcome.featureMetricsDesc,
      color: 'text-green-500 dark:text-green-400',
      bg: 'bg-green-50 dark:bg-green-900/30',
    },
    {
      icon: Bell,
      title: t.welcome.featureAlertsTitle,
      description: t.welcome.featureAlertsDesc,
      color: 'text-amber-500 dark:text-amber-400',
      bg: 'bg-amber-50 dark:bg-amber-900/30',
    },
    {
      icon: MessageSquare,
      title: t.welcome.featureChatTitle,
      description: t.welcome.featureChatDesc,
      color: 'text-purple-500 dark:text-purple-400',
      bg: 'bg-purple-50 dark:bg-purple-900/30',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Hero */}
      <header className="flex-shrink-0 flex flex-col items-center justify-center pt-16 pb-10 px-4 text-center">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/40">
            <Zap className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
            Ключ-Астром <span className="text-blue-600 dark:text-blue-400">MCP</span>
          </h1>
        </div>

        <p className="max-w-2xl text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
          {t.welcome.subtitle}
        </p>

        <button
          onClick={onGetStarted}
          className={clsx(
            'inline-flex items-center px-6 py-3 rounded-lg font-medium text-white',
            'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
            'shadow-lg shadow-blue-600/25 dark:shadow-blue-500/25',
            'transition-all duration-200 transform hover:scale-[1.02]'
          )}
        >
          {t.welcome.getStarted}
          <ChevronRight className="ml-2 h-5 w-5" />
        </button>
      </header>

      {/* Feature cards */}
      <section className="flex-1 max-w-5xl mx-auto w-full px-4 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {features.map(({ icon: Icon, title, description, color, bg }) => (
            <div
              key={title}
              className={clsx(
                'rounded-xl border border-gray-200 dark:border-gray-700',
                'bg-white dark:bg-gray-800 p-6',
                'hover:shadow-md transition-shadow duration-200'
              )}
            >
              <div className={clsx('inline-flex p-3 rounded-lg mb-4', bg)}>
                <Icon className={clsx('h-6 w-6', color)} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-gray-400 dark:text-gray-500 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-center space-x-2">
          <Shield className="h-4 w-4" />
          <span>{t.welcome.footer}</span>
        </div>
      </footer>
    </div>
  );
};
